import { useState, useContext } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  HiUserPlus, 
  HiMagnifyingGlass, 
  HiAdjustmentsHorizontal,
  HiXMark,
  //HiExclamationTriangle,
  // HiTrash
} from 'react-icons/hi2'
import { Input, Select, Button, Loading } from '../../../components'
import { UsersTable } from '../components/UsersTable'
import { CreateUserModal } from '../components/CreateUserModal'
import { EditUserModal } from '../components/EditUserModal'
import { UserDetailsModal } from '../components/UserDetailsModal'
import { DeleteUserModal } from '../components/DeleteUserModal'
import { ManageUserSectorsModal } from '../components/ManageUserSectorsModal'
// import { BulkActionsModal } from '../components/BulkActionsModal'
import { useUsersManagement } from '../hooks/useUsersManagement'
import { AuthContext } from '../../../contexts'
import type { User } from '../../../models'

const FUNCAO_OPTIONS = [
  { value: 'ALL', label: 'Todas as Funções' },
  { value: 'ADMIN', label: 'Administrador' },
  { value: 'DIRETOR', label: 'Diretor' },
  { value: 'USUARIO', label: 'Usuário' }
]

export const CollaboratorsPage = () => {
  // Check if user is ADMIN
  const auth = useContext(AuthContext)
  const currentUser = auth?.user

  const {
    users,
    setores,
    loading,
    loadingSetores,
    filters,
    updateFilters,
    clearFilters,
    createUser,
    updateUser,
    deleteUser,
    vincularSetores,
    desvincularSetor,
    refresh,
    isAdmin, // Receber do hook
  } = useUsersManagement()

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showManageSectorsModal, setShowManageSectorsModal] = useState(false)
  // const [showBulkActionsModal, setShowBulkActionsModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)

  // Bulk selection
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([])

  // Handlers
  const handleView = (user: User) => {
    setSelectedUser(user)
    setShowDetailsModal(true)
  }

  const handleEdit = (user: User) => {
    setSelectedUser(user)
    setShowEditModal(true)
  }

  const handleDelete = (user: User) => {
    setSelectedUser(user)
    setShowDeleteModal(true)
  }

  const handleManageSectors = (user: User) => {
    setSelectedUser(user)
    setShowManageSectorsModal(true)
  }

  const handleConfirmDelete = async (userId: number): Promise<boolean> => {
    setIsDeleting(true)
    const success = await deleteUser(userId)
    setIsDeleting(false)
    return success
  }

  // const handleBulkDelete = () => {
  //   setShowBulkActionsModal(true)
  // }

  // const handleBulkAction = async (action: 'delete' | 'assign-sector', data?: any) => {
  //   // Implementar a lógica para ações em massa aqui
  //   setSelectedUserIds([])
  //   setShowBulkActionsModal(false)
  //   refresh()
  // }

  const handleSelectAll = () => {
    if (selectedUserIds.length === users.length) {
      setSelectedUserIds([])
    } else {
      setSelectedUserIds(users.map(u => u.id))
    }
  }

  const handleSelectUser = (userId: number) => {
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    )
  }

  const setorOptions = [
    { value: '', label: 'Todos os Setores' },
    ...setores.map(setor => ({
      value: setor.id.toString(),
      label: setor.nome_setor
    }))
  ]

  const hasActiveFilters = filters.search || filters.setor || filters.funcao !== 'ALL'

  // Check if user is loaded
  if (!currentUser) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loading message="Carregando..." />
          <p className="text-gray-600 dark:text-gray-400">Carregando dados...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full space-y-4">
      {/* Banner informativo para não-admins */}
      {/* {!isAdmin && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <HiExclamationTriangle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                Visualização Limitada
              </h3>
              <p className="text-sm text-blue-800 dark:text-blue-200">
                Você está visualizando apenas os colaboradores dos seus setores. Apenas administradores podem ver todos os usuários do sistema.
              </p>
            </div>
          </div>
        </div>
      )} */}

      {/* Header Compacto */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-gray-200 dark:border-neutral-700 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              Gerenciamento de Usuários
            </h1>
            <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">
              {loading ? 'Carregando...' : `${users.length} usuário${users.length !== 1 ? 's' : ''} encontrado${users.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* {selectedUserIds.length > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
                className="flex items-center gap-1.5"
              >
                <HiTrash className="w-4 h-4" />
                Ações em Massa ({selectedUserIds.length})
              </Button>
            )} */}
            {isAdmin && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-1.5"
              >
                <HiUserPlus className="w-4 h-4" />
                Novo Usuário
              </Button>
            )}
          </div>
        </div>

        {/* Mini Stats */}
        <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-md px-3 py-2">
            <div className="text-xs text-blue-600 dark:text-blue-400 font-medium">Total</div>
            <div className="text-lg font-bold text-blue-900 dark:text-blue-100">{loading ? '...' : users.length}</div>
          </div>
          <div className="bg-red-50 dark:bg-red-900/20 rounded-md px-3 py-2">
            <div className="text-xs text-red-600 dark:text-red-400 font-medium">Admins</div>
            <div className="text-lg font-bold text-red-900 dark:text-red-100">
              {loading ? '...' : users.filter(u => u.funcao_usuario === 'ADMIN').length}
            </div>
          </div>
          {/* <div className="bg-purple-50 dark:bg-purple-900/20 rounded-md px-3 py-2">
            <div className="text-xs text-purple-600 dark:text-purple-400 font-medium">Diretores</div>
            <div className="text-lg font-bold text-purple-900 dark:text-purple-100">
              {loading ? '...' : users.filter(u => u.funcao_usuario === 'DIRETOR').length}
            </div>
          </div> */}
          <div className="bg-green-50 dark:bg-green-900/20 rounded-md px-3 py-2">
            <div className="text-xs text-green-600 dark:text-green-400 font-medium">Usuários</div>
            <div className="text-lg font-bold text-green-900 dark:text-green-100">
              {loading ? '...' : users.filter(u => u.funcao_usuario === 'USUARIO').length}
            </div>
          </div>
        </div>
      </div>

      {/* Filters Compacto */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-gray-200 dark:border-neutral-700 p-3">
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <HiAdjustmentsHorizontal className="w-4 h-4" />
            Filtros
            {hasActiveFilters && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                Ativos
              </span>
            )}
          </button>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center gap-1"
            >
              <HiXMark className="w-3 h-3" />
              Limpar
            </button>
          )}
        </div>

        <AnimatePresence>
          {filtersOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
                {/* Search */}
                <div className="md:col-span-1">
                  <div className="relative">
                    <HiMagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={filters.search}
                      onChange={(e) => updateFilters({ search: e.target.value })}
                      placeholder="Buscar... (mín. 3 caracteres)"
                      className="pl-8 h-9 text-sm"
                    />
                    {filters.search && filters.search.length < 3 && (
                      <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {3 - filters.search.length} mais
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Setor Filter */}
                <div>
                  <Select
                    options={setorOptions}
                    value={filters.setor?.toString() || ''}
                    onChange={(value) => updateFilters({ setor: value as string })}
                    disabled={loadingSetores}
                    className="h-9 text-sm"
                  />
                </div>

                {/* Função Filter */}
                <div>
                  <Select
                    options={FUNCAO_OPTIONS}
                    value={filters.funcao || 'ALL'}
                    onChange={(value) => updateFilters({ funcao: value as any })}
                    className="h-9 text-sm"
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-gray-200 dark:border-neutral-700 overflow-hidden">
        <UsersTable
          users={users}
          loading={loading}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onManageSectors={handleManageSectors}
          selectedUserIds={selectedUserIds}
          onSelectUser={handleSelectUser}
          onSelectAll={handleSelectAll}
          isAdmin={isAdmin}
        />
      </div>

      {/* Modals */}
      <CreateUserModal
        isOpen={showCreateModal}
        setores={setores}
        onClose={() => setShowCreateModal(false)}
        onSubmit={createUser}
      />

      <EditUserModal
        isOpen={showEditModal}
        user={selectedUser}
        setores={setores}
        onClose={() => {
          setShowEditModal(false)
          setSelectedUser(null)
        }}
        onSubmit={updateUser}
      />

      <UserDetailsModal
        isOpen={showDetailsModal}
        user={selectedUser}
        onClose={() => {
          setShowDetailsModal(false)
          setSelectedUser(null)
        }}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <DeleteUserModal
        isOpen={showDeleteModal}
        user={selectedUser}
        onClose={() => {
          setShowDeleteModal(false)
          setSelectedUser(null)
        }}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />

      <ManageUserSectorsModal
        isOpen={showManageSectorsModal}
        user={selectedUser}
        allSetores={setores}
        onClose={() => {
          setShowManageSectorsModal(false)
          setSelectedUser(null)
          refresh()
        }}
        onVincular={vincularSetores}
        onDesvincular={desvincularSetor}
      />

      {/* <BulkActionsModal
        isOpen={showBulkActionsModal}
        selectedUserIds={selectedUserIds}
        users={users}
        setores={setores}
        onClose={() => setShowBulkActionsModal(false)}
        onConfirm={handleBulkAction}
        onDeleteUsers={deleteUser}
      /> */}
    </div>
  )
}
